import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { QualityBudgets } from "./quality";

/** Base plane edge length in meters before scaling (matches historic geometry). */
export const FLOOR_BASE_SIZE = 20;

export interface SceneGraph {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  /** Physics-aligned ground plane (receives shadows). */
  floor: THREE.Mesh;
  /** Soft practice mat inset above the floor. */
  practiceMat: THREE.Mesh;
  /** 1 m spatial grid (slightly above floor). */
  grid: THREE.GridHelper;
  humanRoot: THREE.Group;
  dispose: () => void;
  resize: () => void;
  /** Apply package environment.floor { y, size }. */
  setFloor: (y: number, sizeMeters: number) => void;
}

/**
 * Procedural studio floor: warm wood-ish boards + soft vignette + faint meter ticks.
 * No external textures (works offline / PWA).
 */
function createStudioFloorMap(resolution = 1024): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = resolution;
  canvas.height = resolution;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    const tex = new THREE.CanvasTexture(canvas);
    return tex;
  }

  // Base warm studio tone
  const base = ctx.createLinearGradient(0, 0, resolution, resolution);
  base.addColorStop(0, "#2a2430");
  base.addColorStop(0.45, "#3a322c");
  base.addColorStop(1, "#241e28");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, resolution, resolution);

  // Wood-ish planks (horizontal bands with noise)
  const plankCount = 28;
  const plankH = resolution / plankCount;
  for (let i = 0; i < plankCount; i++) {
    const y = i * plankH;
    const shade = 0.88 + ((i * 17) % 7) * 0.02;
    const r = Math.floor(58 * shade);
    const g = Math.floor(48 * shade);
    const b = Math.floor(42 * shade);
    ctx.fillStyle = `rgba(${r},${g},${b},0.55)`;
    ctx.fillRect(0, y, resolution, plankH - 1);

    // plank seam
    ctx.strokeStyle = "rgba(0,0,0,0.28)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y + plankH);
    ctx.lineTo(resolution, y + plankH);
    ctx.stroke();

    // grain streaks
    ctx.strokeStyle = "rgba(255,230,200,0.035)";
    for (let s = 0; s < 6; s++) {
      const x0 = ((i * 97 + s * 131) % resolution);
      ctx.beginPath();
      ctx.moveTo(x0, y + 2);
      ctx.lineTo(x0 + plankH * 3, y + plankH - 2);
      ctx.stroke();
    }
  }

  // Soft vignette so the center reads as a stage
  const vig = ctx.createRadialGradient(
    resolution * 0.5,
    resolution * 0.5,
    resolution * 0.15,
    resolution * 0.5,
    resolution * 0.5,
    resolution * 0.72,
  );
  vig.addColorStop(0, "rgba(0,0,0,0)");
  vig.addColorStop(0.65, "rgba(0,0,0,0.12)");
  vig.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, resolution, resolution);

  // Faint 1/10 UV grid (maps to meters when repeat = size)
  ctx.strokeStyle = "rgba(180, 210, 255, 0.07)";
  ctx.lineWidth = 1;
  const cells = 10;
  const step = resolution / cells;
  for (let i = 0; i <= cells; i++) {
    const p = i * step;
    ctx.beginPath();
    ctx.moveTo(p, 0);
    ctx.lineTo(p, resolution);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, p);
    ctx.lineTo(resolution, p);
    ctx.stroke();
  }
  // stronger every 5 cells (≈ half-floor on 20 m default)
  ctx.strokeStyle = "rgba(200, 230, 255, 0.12)";
  for (let i = 0; i <= cells; i += 5) {
    const p = i * step;
    ctx.beginPath();
    ctx.moveTo(p, 0);
    ctx.lineTo(p, resolution);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, p);
    ctx.lineTo(resolution, p);
    ctx.stroke();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function createPracticeMatMap(resolution = 512): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = resolution;
  canvas.height = resolution;
  const ctx = canvas.getContext("2d");
  if (!ctx) return new THREE.CanvasTexture(canvas);

  // Yoga-mat style: deep teal with subtle fabric noise + border
  ctx.fillStyle = "#1a4d4a";
  ctx.fillRect(0, 0, resolution, resolution);

  for (let i = 0; i < 4000; i++) {
    const x = (i * 47) % resolution;
    const y = (i * 91) % resolution;
    const a = 0.02 + (i % 5) * 0.008;
    ctx.fillStyle = `rgba(255,255,255,${a})`;
    ctx.fillRect(x, y, 2, 2);
  }

  // Inner border
  const pad = Math.floor(resolution * 0.04);
  ctx.strokeStyle = "rgba(120, 220, 200, 0.35)";
  ctx.lineWidth = Math.max(2, resolution * 0.012);
  ctx.strokeRect(pad, pad, resolution - pad * 2, resolution - pad * 2);

  // Soft center highlight
  const g = ctx.createRadialGradient(
    resolution * 0.5,
    resolution * 0.5,
    resolution * 0.05,
    resolution * 0.5,
    resolution * 0.5,
    resolution * 0.55,
  );
  g.addColorStop(0, "rgba(80, 180, 160, 0.18)");
  g.addColorStop(1, "rgba(0,0,0,0.25)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, resolution, resolution);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  return tex;
}

export function createSceneGraph(canvas: HTMLCanvasElement, budgets: QualityBudgets): SceneGraph {
  const scene = new THREE.Scene();
  // Slightly warmer studio backdrop so the floor reads as a room, not void.
  scene.background = new THREE.Color(0x12151f);
  scene.fog = new THREE.Fog(0x12151f, 12, 48);

  const camera = new THREE.PerspectiveCamera(45, 1, 0.05, 200);
  camera.position.set(0, 1.55, 3.6);
  camera.lookAt(0, 0.9, 0);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: budgets.tier !== "low",
    alpha: false,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, budgets.maxPixelRatio));
  renderer.shadowMap.enabled = budgets.shadows;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  if ("outputColorSpace" in renderer) {
    (renderer as THREE.WebGLRenderer).outputColorSpace = THREE.SRGBColorSpace;
  }

  const controls = new OrbitControls(camera, canvas);
  controls.target.set(0, 0.9, 0);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = 0.5;
  controls.maxDistance = 40;
  controls.maxPolarAngle = Math.PI; // allow true aerial / top-down
  controls.update();

  const hemi = new THREE.HemisphereLight(0xe8f0ff, 0x3a2f28, 0.95);
  scene.add(hemi);

  const key = new THREE.DirectionalLight(0xfff2e0, 1.25);
  key.position.set(3.2, 5.5, 2.4);
  key.castShadow = budgets.shadows;
  if (budgets.shadows) {
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.near = 0.5;
    key.shadow.camera.far = 24;
    key.shadow.camera.left = -8;
    key.shadow.camera.right = 8;
    key.shadow.camera.top = 8;
    key.shadow.camera.bottom = -8;
    key.shadow.bias = -0.00025;
  }
  scene.add(key);

  const fill = new THREE.DirectionalLight(0xa8c4ff, 0.35);
  fill.position.set(-3, 2.5, -2);
  scene.add(fill);

  // --- Studio floor ---
  const floorMap = createStudioFloorMap(budgets.tier === "low" ? 512 : 1024);
  floorMap.repeat.set(1, 1);
  const floorGeo = new THREE.PlaneGeometry(FLOOR_BASE_SIZE, FLOOR_BASE_SIZE, 1, 1);
  const floorMat = new THREE.MeshStandardMaterial({
    map: floorMap,
    color: 0xffffff,
    roughness: 0.82,
    metalness: 0.04,
  });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = budgets.shadows;
  floor.name = "studioFloor";
  scene.add(floor);

  // --- Practice mat (acro-friendly inset) ---
  const matMap = createPracticeMatMap(budgets.tier === "low" ? 256 : 512);
  const matSize = 3.2; // meters — typical large yoga/acro mat footprint
  const matGeo = new THREE.PlaneGeometry(matSize, matSize * 1.35);
  const matMat = new THREE.MeshStandardMaterial({
    map: matMap,
    color: 0xffffff,
    roughness: 0.92,
    metalness: 0.0,
  });
  const practiceMat = new THREE.Mesh(matGeo, matMat);
  practiceMat.rotation.x = -Math.PI / 2;
  practiceMat.position.y = 0.004; // avoid z-fight with floor
  practiceMat.receiveShadow = budgets.shadows;
  practiceMat.name = "practiceMat";
  scene.add(practiceMat);

  // --- Meter grid for scale (subtle) ---
  const grid = new THREE.GridHelper(FLOOR_BASE_SIZE, FLOOR_BASE_SIZE, 0x5a7a9a, 0x2a3548);
  grid.position.y = 0.002;
  const gridMats = Array.isArray(grid.material) ? grid.material : [grid.material];
  for (const m of gridMats) {
    const mat = m as THREE.Material;
    mat.transparent = true;
    mat.opacity = 0.28;
    mat.depthWrite = false;
  }
  grid.name = "floorGrid";
  scene.add(grid);

  const humanRoot = new THREE.Group();
  scene.add(humanRoot);

  const setFloor = (y: number, sizeMeters: number) => {
    const size = Math.max(1, sizeMeters || FLOOR_BASE_SIZE);
    const scale = size / FLOOR_BASE_SIZE;
    floor.position.y = y;
    floor.scale.setScalar(scale);
    // Keep 1 UV tile ≈ full floor (board pattern scales with room).
    floorMap.repeat.set(1, 1);
    floorMap.needsUpdate = true;

    grid.position.y = y + 0.002;
    grid.scale.setScalar(scale);

    practiceMat.position.y = y + 0.004;
    // Mat stays fixed real-world size; only rides floor height.
    practiceMat.scale.set(1, 1, 1);
  };

  // Defaults match runtime when manifest omits environment.floor
  setFloor(0, FLOOR_BASE_SIZE);

  const resize = () => {
    const parent = canvas.parentElement;
    const width = Math.max(1, parent?.clientWidth || canvas.clientWidth || 640);
    const height = Math.max(1, parent?.clientHeight || canvas.clientHeight || 360);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  };
  resize();

  const dispose = () => {
    controls.dispose();
    renderer.dispose();
    floorMap.dispose();
    matMap.dispose();
    floorGeo.dispose();
    floorMat.dispose();
    matGeo.dispose();
    matMat.dispose();
    grid.geometry.dispose();
    for (const m of gridMats) m.dispose();
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry && mesh !== floor && mesh !== practiceMat && mesh !== grid) {
        mesh.geometry.dispose();
      }
      const mat = mesh.material;
      if (mesh === floor || mesh === practiceMat || mesh === grid) return;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else if (mat) mat.dispose();
    });
  };

  return {
    scene,
    camera,
    renderer,
    controls,
    floor,
    practiceMat,
    grid,
    humanRoot,
    dispose,
    resize,
    setFloor,
  };
}
